'use client';

// Category-grouped table/row layout for the Documents tab workbench (design
// Section 6.1/6.7). Replaces the old DocSlot card grid. Reuses
// ../media/hooks/useBreakpoint for responsive behavior — below `md`, the
// table stacks into CSS rows using the same DocumentRow components rather
// than a separate mobile component.

import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useBreakpoint } from '../media/hooks/useBreakpoint';
import { DocumentRow } from './DocumentRow';
import type { DocDef, DocRecord, ExtractionSummary } from './types';
import type { RequestContext } from '../media/requestContext';

export interface DocumentsWorkbenchProps {
  documentGroups: { group: string; docs: DocDef[] }[];
  docs: DocRecord[];
  extractions: Map<string, ExtractionSummary>;
  jobId: string;
  onUploaded: (rec: DocRecord) => void;
  onDeleted: (id: string) => void;
  onView: (rec: DocRecord) => void;
  mode?: 'staff' | 'portal';
  context?: RequestContext;
  // Claim Office role: view-only — see DocumentRow's readOnly prop.
  readOnly?: boolean;
  // Garage delete-window restriction — see DocumentRow's deleteDisabled prop.
  deleteDisabled?: boolean;
}

export function DocumentsWorkbench({
  documentGroups,
  docs,
  extractions,
  jobId,
  onUploaded,
  onDeleted,
  onView,
  mode,
  context,
  readOnly,
  deleteDisabled,
}: DocumentsWorkbenchProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoint(containerRef);
  const isStacked = breakpoint !== 'desktop';

  // Every category starts expanded; collapse state does not persist across
  // reloads/tab switches (session-local UI state only).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleGroup = (group: string) =>
    setCollapsed(prev => ({ ...prev, [group]: !prev[group] }));

  return (
    <div ref={containerRef} data-breakpoint={breakpoint} className="flex flex-col gap-4">
      {/* Column header — shown whenever DocumentRow itself renders as a grid
          (its own `sm:` breakpoint, viewport-based), not gated on the
          container-width useBreakpoint below: the sidebar can squeeze this
          column's container under 1024px on common laptop widths while the
          row grid itself still renders fine at `sm:` and up. */}
      <div
        className="hidden sm:grid grid-cols-[minmax(0,2fr)_auto_auto_minmax(0,1.5fr)_auto] gap-3 border-b border-gray-200 px-4 pb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400"
      >
        <span>Document</span>
        <span>Status</span>
        <span>Confidence</span>
        <span>File</span>
        <span>Actions</span>
      </div>

      {documentGroups.map(group => {
        const uploadedCount = docs.filter(d => group.docs.some(gd => gd.key === d.doc_type)).length;
        const isCollapsed = !!collapsed[group.group];

        return (
          <div key={group.group} className="rounded-lg border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => toggleGroup(group.group)}
              aria-expanded={!isCollapsed}
              className="flex w-full items-center gap-2.5 border-b border-gray-100 px-4 py-2 text-left last:border-b-0"
            >
              <svg
                className={cn('h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform', isCollapsed ? '-rotate-90' : '')}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900">{group.group}</h3>
              <div className="h-px flex-1 bg-gray-100" />
              <span className="text-xs text-gray-400">
                {uploadedCount}/{group.docs.length} uploaded
              </span>
            </button>

            {!isCollapsed && (
              <div className={cn('flex flex-col', isStacked && 'divide-y divide-gray-100')}>
                {group.docs.map(doc => {
                  const existing = docs.find(d => d.doc_type === doc.key);
                  return (
                    <DocumentRow
                      key={doc.key}
                      doc={doc}
                      jobId={jobId}
                      existing={existing}
                      extraction={existing ? extractions.get(existing.id) : undefined}
                      onUploaded={onUploaded}
                      onDeleted={onDeleted}
                      onView={onView}
                      mode={mode}
                      context={context}
                      readOnly={readOnly}
                      deleteDisabled={deleteDisabled}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
